/**
 * 통합관리자 — 바닥글
 * ---------------------------------------------------------------------------
 * 모든 화면 아래에 찍히는 공급사 사업자 정보를 고친다. 기업은 이 화면을 볼 수
 * 없다 — 공급사 한 곳의 정보이지 고객사의 정보가 아니다.
 *
 * 왜 화면이 필요한가
 *   예전에는 빌드 시점 환경변수(VITE_FOOTER_*)에 박혀 있었다. 주소나 전화가
 *   바뀌면 **웹을 다시 빌드해서 배포**해야 했는데, 전자상거래법상 표시 의무가
 *   있는 값이라 그동안 틀린 정보가 떠 있게 된다.
 *
 * 비워 두면 그 줄이 사라진다
 *   바닥글은 값이 없는 항목을 통째로 뺀다. 그래서 「팩스: 」 같은 빈 칸이
 *   남지 않는다. 화면에서도 그 점을 알려 준다.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App as AntdApp, Button, Card, Col, Form, Input, Row, Space, Typography } from 'antd';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

interface Footer {
  companyName: string | null;
  ceoName: string | null;
  address: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  bizRegNo: string | null;
  mailOrderNo: string | null;
  privacyUrl: string | null;
  termsUrl: string | null;
  copyright: string | null;
  updatedAt: string | null;
}

/**
 * 조회값을 폼에 넣을 모양으로 바꾼다.
 *
 * 비어 있는 칸은 서버가 null 로 준다. 그대로 `Input` 에 넣으면 React 가 그
 * 칸을 비제어로 보고 경고를 내고, 저장할 때도 null 이 그대로 되나간다. 빈
 * 문자열이면 「지우라」는 뜻으로 서버가 똑같이 읽는다.
 *
 * `updatedAt` 은 폼 칸이 아니지만 setFieldsValue 가 저장소에 남겨 두면
 * 저장할 때 함께 나간다. 보낼 것만 담는다.
 */
function toForm(data: Footer | undefined): Partial<Footer> {
  return Object.fromEntries(
    Object.entries(data ?? {})
      .filter(([key]) => key !== 'updatedAt')
      .map(([key, value]) => [key, value ?? '']),
  );
}

/** 미리보기는 실제 바닥글과 같은 규칙으로 줄을 만든다(빈 항목은 뺀다). */
function lineOf(pairs: Array<[string, string | undefined]>): string {
  return pairs
    .filter(([, v]) => (v ?? '').trim() !== '')
    .map(([label, v]) => `${label}: ${String(v).trim()}`)
    .join('  |  ');
}

export default function FooterPage() {
  const client = useQueryClient();
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm<Footer>();
  const [saving, setSaving] = useState(false);
  // 미리보기를 그리려면 입력 중인 값을 알아야 한다.
  const [draft, setDraft] = useState<Partial<Footer>>({});

  const footer = useQuery<Footer, NormalizedError>({
    queryKey: ['admin', 'footer'],
    queryFn: async () => (await apiClient.get<Footer>('/admin/footer')).data,
  });

  useEffect(() => {
    if (!footer.data) return;
    const values = toForm(footer.data);
    form.setFieldsValue(values);
    setDraft(values);
  }, [footer.data, form]);

  const save = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;

    setSaving(true);
    try {
      // 빈 칸은 "지우라" 는 뜻으로 서버가 받는다(빈 문자열 → null).
      await apiClient.put('/admin/footer', values);
      message.success('바닥글을 저장했습니다. 모든 화면에 곧 반영됩니다.');
      await client.invalidateQueries({ queryKey: ['admin', 'footer'] });
      // 지금 보고 있는 바닥글도 새로 읽게 한다.
      await client.invalidateQueries({ queryKey: ['public', 'footer'] });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setSaving(false);
    }
  };

  const d = draft;
  const contacts = lineOf([
    ['대표이사', d.ceoName ?? undefined],
    ['대표전화', d.phone ?? undefined],
    ['팩스', d.fax ?? undefined],
    ['이메일', d.email ?? undefined],
  ]);
  const business = lineOf([
    ['사업자등록번호', d.bizRegNo ?? undefined],
    ['통신판매업 신고번호', d.mailOrderNo ?? undefined],
  ]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 980 }}>
      <Alert
        type="info"
        showIcon
        message="모든 화면 아래에 찍히는 공급사 정보입니다"
        description={
          <>
            로그인 화면을 포함한 <b>모든 화면</b>과 고객에게 보이는 자리에 그대로 나옵니다.
            유료 구독을 받는 서비스는 상호·대표자·주소·전화·사업자등록번호·통신판매업
            신고번호를 이용자가 쉽게 볼 수 있는 곳에 표시할 의무가 있습니다.
            <br />
            비워 둔 항목은 <b>그 줄째 표시되지 않습니다</b> — 빈 칸이 남지 않습니다.
          </>
        }
      />

      <Card title="바닥글" loading={footer.isLoading}>
        <Form form={form} layout="vertical" onValuesChange={(_, all) => setDraft(all)}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="companyName" label="회사명">
                <Input placeholder="(주)워크앤아이티" maxLength={200} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="ceoName" label="대표이사">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="address" label="주소">
                <Input placeholder="서울특별시 ..." maxLength={300} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="phone" label="대표전화">
                <Input placeholder="02-0000-0000" maxLength={40} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="fax" label="팩스">
                <Input placeholder="02-0000-0001" maxLength={40} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="email"
                label="이메일"
                rules={[{ type: 'email', message: '이메일 형식이 올바르지 않습니다' }]}
              >
                <Input placeholder="help@worknit.co.kr" maxLength={254} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="bizRegNo"
                label="사업자등록번호"
                extra="바닥글에 찍히는 그대로 적습니다. 하이픈을 넣어도 됩니다."
              >
                <Input placeholder="123-45-67890" maxLength={40} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="mailOrderNo" label="통신판매업 신고번호">
                <Input placeholder="제2026-서울강남-00000호" maxLength={80} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="privacyUrl"
                label="개인정보처리방침 주소"
                rules={[{ type: 'url', message: 'http(s):// 로 시작하는 주소를 입력하세요' }]}
              >
                <Input placeholder="https://..." maxLength={500} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="termsUrl"
                label="이용약관 주소"
                rules={[{ type: 'url', message: 'http(s):// 로 시작하는 주소를 입력하세요' }]}
              >
                <Input placeholder="https://..." maxLength={500} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="copyright" label="Copyright">
                <Input placeholder="© 2026 WORK&IT Co., Ltd. All rights reserved." maxLength={300} />
              </Form.Item>
            </Col>
          </Row>

          <Space>
            <Button type="primary" onClick={save} loading={saving}>
              저장
            </Button>
            <Button
              onClick={() => {
                const values = toForm(footer.data);
                form.setFieldsValue(values);
                setDraft(values);
              }}
            >
              되돌리기
            </Button>
            {footer.data?.updatedAt && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                마지막 수정 {new Date(footer.data.updatedAt).toLocaleString('ko-KR')}
              </Text>
            )}
          </Space>
        </Form>
      </Card>

      {/* 저장하기 전에 실제로 어떻게 보이는지 같은 모양으로 보여 준다. */}
      <Card title="미리보기" size="small">
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          실제 바닥글과 같은 규칙으로 그립니다. 비어 있는 줄은 나타나지 않습니다.
        </Paragraph>
        <div
          style={{
            background: '#0f172a',
            color: 'rgba(255,255,255,0.65)',
            fontSize: 12,
            lineHeight: 1.9,
            padding: '20px 24px',
            borderRadius: 6,
          }}
        >
          {d.companyName && (
            <div style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: 13 }}>
              {d.companyName}
            </div>
          )}
          {d.address && <div>{d.address}</div>}
          {contacts && <div>{contacts}</div>}
          {business && <div>{business}</div>}
          {d.copyright && <div style={{ color: 'rgba(255,255,255,0.45)' }}>{d.copyright}</div>}
          {!d.companyName && !d.address && !contacts && !business && !d.copyright && (
            <div style={{ color: 'rgba(255,255,255,0.45)' }}>
              (값이 하나도 없어 바닥글이 표시되지 않습니다)
            </div>
          )}
        </div>
      </Card>
    </Space>
  );
}
